import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface User {
  id: string;
  username: string;
  email?: string;
  role: string;
  permissions?: string[];
  last_login?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  login: (user: User) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  isAuthenticated: false,

  login: (user) => {
    set({ user, isAuthenticated: true });
  },

  logout: () => {
    set({ user: null, isAuthenticated: false });
  },
}));

interface WarehouseItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  location: string;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface WarehouseState {
  items: WarehouseItem[];
  selectedItem: WarehouseItem | null;
  filters: {
    search?: string;
    location?: string;
    lowStock?: boolean;
  };
  setItems: (items: WarehouseItem[]) => void;
  addItem: (item: WarehouseItem) => void;
  updateItem: (id: string, updates: Partial<WarehouseItem>) => void;
  removeItem: (id: string) => void;
  setSelectedItem: (item: WarehouseItem | null) => void;
  setFilters: (filters: Partial<WarehouseState["filters"]>) => void;
  clearFilters: () => void;
}

export const useWarehouseStore = create<WarehouseState>((set, get) => ({
  items: [],
  selectedItem: null,
  filters: {
    search: "",
    location: "",
    lowStock: false,
  },

  setItems: (items) => {
    set({ items });
  },

  addItem: (item) => {
    set((state) => ({
      items: [...state.items, item],
    }));
  },

  updateItem: (id, updates) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id ? { ...item, ...updates, updatedAt: new Date() } : item
      ),
      selectedItem:
        state.selectedItem?.id === id
          ? { ...state.selectedItem, ...updates, updatedAt: new Date() }
          : state.selectedItem,
    }));
  },

  removeItem: (id) => {
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
      selectedItem: state.selectedItem?.id === id ? null : state.selectedItem,
    }));
  },

  setSelectedItem: (item) => {
    set({ selectedItem: item });
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        search: "",
        location: "",
        lowStock: false,
      },
    });
  },
}));

interface UIState {
  sidebarCollapsed: boolean;
  theme: "light" | "dark";
  notifications: Array<{
    id: string;
    type: "success" | "error" | "warning" | "info";
    message: string;
    timestamp: Date;
  }>;
  toggleSidebar: () => void;
  setTheme: (theme: "light" | "dark") => void;
  addNotification: (
    notification: Omit<UIState["notifications"][0], "id" | "timestamp">
  ) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      theme: "light",
      notifications: [],

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setTheme: (theme) => {
        set({ theme });
      },

      addNotification: (notification) => {
        const id = Date.now().toString();
        set((state) => ({
          notifications: [
            ...state.notifications,
            { ...notification, id, timestamp: new Date() },
          ],
        }));

        // Auto-remove notification after 5 seconds
        setTimeout(() => {
          set((state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }));
        }, 5000);
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },
    }),
    {
      name: "ui-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
);
